import type {
    VegobjekterStatistikk,
    VegobjekterStatistikkGruppert,
} from '@/generated/nvdb/vegobjekter/models';
import { VegobjekterStatisticsResponse } from '../responses';
import { AllVegnettStatisticsResponseGruppert } from '../responses/VegnettStatisticsResponse';
import {
    AllVegobjekterStatisticsResponseGruppert,
    VegobjekterStatisticsByKategoriResponseGruppert,
    VegobjekterStatisticsResponseGruppertByEgenskap,
    VegobjekterStatisticsResponseGruppertByFylke,
    VegobjekterStatisticsResponseGruppertByKommune,
} from '../responses/VegobjekterStatisticsResponseGruppert';

type VegnettStatisticsGruppert = {
    antall: number;
    vegkategori: string;
    fylke: number;
};

type VegobjekterStatisticsWithValues = VegobjekterStatistikk & {
    antall: number;
    lengde: number;
};

type VegobjekterStatisticsByFylke = VegobjekterStatistikkGruppert & {
    antall: number;
    lengde: number;
    fylke: number;
};

type VegobjekterStatisticsByKommune = VegobjekterStatistikkGruppert & {
    antall: number;
    lengde: number;
    kommune: number;
};

type VegobjekterStatisticsByKategori = VegobjekterStatistikkGruppert & {
    antall: number;
    lengde: number;
    vegkategori: string;
};

type AllVegobjekterStatistics = VegobjekterStatistikkGruppert & {
    antall: number;
    lengde: number;
    vegkategori: string;
    fylke: number;
    kommune: number;
};

type VegobjekterStatisticsByEgenskap = VegobjekterStatistikkGruppert & {
    antall: number;
    lengde: number;
    egenskapsVerdi?: string;
};

export function transformVegnettStatisticsGruppert(
    data: VegnettStatisticsGruppert[] | null
): AllVegnettStatisticsResponseGruppert[] | null {
    if (!data) return null;

    return data.map(
        vegnett =>
            new AllVegnettStatisticsResponseGruppert(
                vegnett.antall,
                vegnett.vegkategori,
                vegnett.fylke
            )
    );
}

export function transformVegobjekterStatistics(
    data: (VegobjekterStatisticsWithValues & { antall?: number }) | null
): VegobjekterStatisticsResponse | null {
    if (data && data.antall !== undefined)
        return new VegobjekterStatisticsResponse(data.antall, data.lengde);
    else return null;
}

export function transformVegobjekterStatisticsGruppertByFylke(
    data: VegobjekterStatisticsByFylke[] | null
): VegobjekterStatisticsResponseGruppertByFylke[] | null {
    if (!data) return null;

    return data.map(
        vegobjekt =>
            new VegobjekterStatisticsResponseGruppertByFylke(
                vegobjekt.antall,
                vegobjekt.lengde,
                vegobjekt.fylke
            )
    );
}

export function transformVegobjekterStatisticsGruppertByKommune(
    data: VegobjekterStatisticsByKommune[] | null
): VegobjekterStatisticsResponseGruppertByKommune[] | null {
    if (!data) return null;

    return data.map(
        vegobjekt =>
            new VegobjekterStatisticsResponseGruppertByKommune(
                vegobjekt.antall,
                vegobjekt.lengde,
                vegobjekt.kommune
            )
    );
}

export function transformVegobjekterStatisticsByKategoriGruppert(
    data: VegobjekterStatisticsByKategori[] | null
): VegobjekterStatisticsByKategoriResponseGruppert[] | null {
    if (!data) return null;

    return data.map(
        vegobjekt =>
            new VegobjekterStatisticsByKategoriResponseGruppert(
                vegobjekt.antall,
                vegobjekt.lengde,
                vegobjekt.vegkategori
            )
    );
}

export function transformAllVegobjekterStatisticsGruppert(
    data: AllVegobjekterStatistics[] | null
): AllVegobjekterStatisticsResponseGruppert[] | null {
    if (!data) return null;

    return data.map(
        vegobjekt =>
            new AllVegobjekterStatisticsResponseGruppert(
                vegobjekt.antall,
                vegobjekt.lengde,
                vegobjekt.vegkategori,
                vegobjekt.fylke,
                vegobjekt.kommune
            )
    );
}

export function transformVegobjekterStatisticsGruppertByEgenskap(
    data: VegobjekterStatisticsByEgenskap[] | null
): VegobjekterStatisticsResponseGruppertByEgenskap[] | null {
    if (!data) return null;

    return data.map(
        item =>
            new VegobjekterStatisticsResponseGruppertByEgenskap(
                item.antall,
                item.lengde,
                item.egenskapsVerdi ?? null
            )
    );
}
